/**
 * Draft edits to the beer-modal tank strip.
 * Stock is re-simulated from the drafted arrivals (a stockout floors at zero),
 * so the weekly line matches the backend planner.
 */
import brewFromRecommendation from '../brewFromRecommendation';

const DAY_MS = 86400000;

const parseDate = (ds) => new Date(`${String(ds).slice(0, 10)}T00:00:00Z`);

export const addDays = (ds, n) => {
  const next = new Date(parseDate(ds).getTime() + n * DAY_MS);
  return next.toISOString().slice(0, 10);
};

export const diffDays = (a, b) => Math.round(
  (parseDate(a).getTime() - parseDate(b).getTime()) / DAY_MS,
);

const lossFactor = (lossPct) => (
  1 - Math.min(Math.max(Number(lossPct) || 0, 0), 90) / 100
);

/** Packaged litres that land in stock, before display rounding. */
export const packagedFromVolume = (volumeLitres, lossPct) => (
  (Number(volumeLitres) || 0) * lossFactor(lossPct)
);

/** Brew volume snapped to 1,000 L, never below one step. */
export const snapVolume = (litres) => {
  const n = Number(litres);
  if (!Number.isFinite(n)) return 1000;
  return Math.max(1000, Math.round(n / 1000) * 1000);
};

const niceCeil = (value) => {
  if (!(value > 0)) return 1000;
  const exp = 10 ** Math.floor(Math.log10(value));
  const n = value / exp;
  let step = 10;
  if (n <= 1) step = 1;
  else if (n <= 2) step = 2;
  else if (n <= 5) step = 5;
  return step * exp;
};

/** Tank strip never needs to show above this. niceCeil(25k × 1.15) would otherwise hit 50k. */
const TANK_Y_CAP = 30000;

/**
 * Litres a strip segment draws.
 * Brew week shows tank size. Ready week (and a same-week bar) shows packaged yield.
 */
export const segmentLitres = (item, role) => (
  role === 'brew'
    ? (Number(item.volumeLitres) || Number(item.packagedLitres) || 0)
    : (Number(item.packagedLitres) || 0)
);

/**
 * Frozen tank-axis max: headroom over the tallest stack and the largest
 * configured tank, so a resize does not rescale the axis under the cursor.
 * Brew week counts tank size; the ready week counts packaged yield.
 */
export const tankYMax = (arrivals, plan) => {
  const byWeek = {};
  const add = (week, litres) => {
    if (week == null || week < 0 || !(litres > 0)) return;
    byWeek[week] = (byWeek[week] || 0) + litres;
  };
  (arrivals || []).forEach((item) => {
    const brewWeek = item.brewWeekIdx;
    const split = brewWeek != null && brewWeek !== item.weekIdx;
    if (split) add(brewWeek, segmentLitres(item, 'brew'));
    add(item.weekIdx, segmentLitres(item, split ? 'ready' : 'single'));
  });
  const stacked = Object.values(byWeek);
  const largestStack = stacked.length ? Math.max(...stacked) : 0;
  const sizes = (plan?.settings?.tanks || [])
    .flatMap((tank) => tank.sizes || [])
    .map((size) => Number(size))
    .filter((size) => size > 0);
  const largestTank = sizes.length ? Math.max(...sizes) : 0;
  const raw = Math.max(largestStack, largestTank, 1000) * 1.15;
  return Math.min(TANK_Y_CAP, niceCeil(raw));
};

/** Week index of a date. Null when it lands after the horizon (it is not on the chart). */
export const weekIndex = (date, weekStarts) => {
  if (!date || !weekStarts || !weekStarts.length) return null;
  const raw = Math.floor(diffDays(date, weekStarts[0]) / 7);
  if (raw >= weekStarts.length) return null;
  return Math.max(raw, 0);
};

/** Week of a brew date. Negative when the brew is before the chart; null past the horizon. */
const brewWeekIndex = (date, weekStarts) => {
  if (!date || !weekStarts || !weekStarts.length) return null;
  const raw = Math.floor(diffDays(date, weekStarts[0]) / 7);
  if (raw >= weekStarts.length) return null;
  return raw;
};

export const recommendationId = (rec, index) => (
  `rec:${index}:${rec.brewDate}:${rec.tankLitres}:${rec.brewery}`
);

const lossPctOf = (plan) => plan?.settings?.packagingLossPct || 0;

/**
 * One draggable (or locked) segment per tank gyle, counted brew and recommendation.
 * `packagedLitres` matches the litres `simulate` added into that week.
 */
export const buildArrivals = (beer, plan) => {
  const weekStarts = plan.weekStarts || [];
  const lossPct = lossPctOf(plan);
  const items = [];

  (beer.tankGyles || []).forEach((gyle, index) => {
    const weekIdx = weekIndex(gyle.eta, weekStarts);
    if (weekIdx == null) return;
    items.push({
      id: `tank:${index}:${gyle.gyleCode || gyle.sku || 'gyle'}`,
      kind: 'tank',
      weekIdx,
      brewWeekIdx: brewWeekIndex(gyle.brewDate, weekStarts),
      volumeLitres: gyle.availableLitres || 0,
      packagedLitres: gyle.packagedLitres || 0,
      editable: false,
      source: gyle,
    });
  });

  (beer.plannedBrews || []).forEach((brew) => {
    if (!brew.counted) return;
    if (brew.status !== 'planned' && brew.status !== 'brewing') return;
    const weekIdx = weekIndex(brew.eta, weekStarts);
    if (weekIdx == null) return;
    items.push({
      id: `brew:${brew.id}`,
      kind: 'brew',
      weekIdx,
      brewWeekIdx: brewWeekIndex(brew.brewDate, weekStarts),
      volumeLitres: brew.volumeLitres,
      packagedLitres: packagedFromVolume(brew.volumeLitres, lossPct),
      editable: brew.status === 'planned',
      source: brew,
    });
  });

  (beer.recommendations || []).forEach((rec, index) => {
    const weekIdx = weekIndex(rec.arrivalDate, weekStarts);
    if (weekIdx == null) return;
    items.push({
      id: recommendationId(rec, index),
      kind: 'rec',
      weekIdx,
      brewWeekIdx: brewWeekIndex(rec.brewDate, weekStarts),
      volumeLitres: rec.tankLitres,
      packagedLitres: packagedFromVolume(rec.tankLitres, lossPct),
      editable: true,
      source: rec,
    });
  });

  return items;
};

/**
 * Week shift whose arrival stays inside the horizon and whose brew date is not
 * before today. Delta 0 (the saved position) is always kept, even if overdue.
 */
export const clampWeekDelta = (item, delta, plan) => {
  const horizon = (plan.weekStarts || []).length;
  const minByHorizon = -item.weekIdx;
  const maxByHorizon = horizon - 1 - item.weekIdx;
  let d = Math.round(Number(delta) || 0);
  if (!Number.isFinite(d)) d = 0;
  d = Math.min(Math.max(d, minByHorizon), maxByHorizon) || 0;

  const { brewDate } = item.source || {};
  const { today } = plan;
  if (!brewDate || !today || d === 0) return d;

  const minByToday = Math.ceil(diffDays(today, brewDate) / 7);
  if (addDays(brewDate, d * 7) >= today) return d;
  if (d < 0) return Math.max(d, Math.min(minByToday, 0), minByHorizon);
  return Math.min(Math.max(minByToday, 0), maxByHorizon);
};

export const clampWeekIndex = (weekIdx, plan) => {
  const horizon = (plan.weekStarts || []).length;
  let week = Math.round(Number(weekIdx) || 0);
  if (!Number.isFinite(week)) week = 0;
  if (!horizon) return 0;
  return Math.min(Math.max(week, 0), horizon - 1);
};

const datesForWeek = (weekIdx, leadTimeDays, plan) => {
  const arrival = (plan.weekStarts || [])[weekIdx] || plan.today;
  let brewDate = addDays(arrival, -Math.round(Number(leadTimeDays) || 0));
  if (plan.today && brewDate < plan.today) brewDate = plan.today;
  return { brewDate, packageDate: arrival };
};

/** Starting point for a brew that exists only in the draft. */
export const defaultAddDraft = (beer, plan) => {
  const tanks = (plan.settings && plan.settings.tanks) || [];
  const brewery = (beer.rules && beer.rules.preferredBrewery)
    || (plan.settings && plan.settings.defaultBrewery)
    || (tanks[0] && tanks[0].brewery)
    || 'B-Studio';
  const match = tanks.find((tank) => tank.brewery === brewery) || tanks[0] || {};
  const sizes = (match.sizes || []).map((size) => Number(size)).filter((size) => size > 0);
  const leadTimeDays = (beer.rules && beer.rules.leadTimeDays != null)
    ? beer.rules.leadTimeDays
    : ((plan.settings && plan.settings.leadTimeDays) || 0);
  return {
    added: true,
    weekIdx: 0,
    volumeLitres: sizes.length ? Math.min(...sizes) : 1000,
    brewery,
    beerName: beer.beerName,
    leadTimeDays,
  };
};

const addedArrival = (id, draft, plan) => {
  const weekIdx = clampWeekIndex(draft.weekIdx, plan);
  const volumeLitres = Math.round(Number(draft.volumeLitres));
  const { brewDate, packageDate } = datesForWeek(weekIdx, draft.leadTimeDays, plan);
  return {
    id,
    kind: 'brew',
    weekIdx,
    brewWeekIdx: brewWeekIndex(brewDate, plan.weekStarts),
    volumeLitres,
    packagedLitres: packagedFromVolume(volumeLitres, lossPctOf(plan)),
    editable: true,
    added: true,
    edited: true,
    weekDelta: 0,
    source: {
      beerName: draft.beerName,
      brewery: draft.brewery,
      brewDate,
      packageDate,
      volumeLitres,
      status: 'planned',
    },
  };
};

/**
 * Recommendation source plus the ready date the schedule row reads.
 * `arrivalDate` stays the original so save applies `weekDelta` once.
 */
const readySource = (item, weekDelta) => {
  const source = { ...(item.source || {}) };
  const base = source.arrivalDate || source.packageDate;
  if (!base) return source;
  source.packageDate = weekDelta ? addDays(base, weekDelta * 7) : base;
  return source;
};

const acceptedRec = (item) => ({
  ...item,
  kind: 'brew',
  draftFromRec: true,
  weekDelta: 0,
  edited: true,
  source: readySource(item, 0),
});

/**
 * Apply drafts. `{ weekIdx, volumeLitres }` moves or resizes. `{ removed: true }` drops the bar.
 * `{ added: true, ... }` is a new brew that is not on the server yet.
 * `{ accepted: true }` turns a recommendation into a planned brew without moving it.
 * An edited recommendation becomes planned.
 */
export const applyDrafts = (arrivals, drafts, plan) => {
  const lossPct = lossPctOf(plan);
  const kept = [];
  arrivals.forEach((item) => {
    const draft = drafts && drafts[item.id];
    if (draft && draft.removed && item.editable) return;
    if (!draft || !item.editable) {
      kept.push(item);
      return;
    }
    const volumeLitres = Math.round(Number(draft.volumeLitres));
    if (!Number.isFinite(volumeLitres) || volumeLitres <= 0) {
      kept.push(item);
      return;
    }
    const requested = draft.weekIdx == null ? item.weekIdx : draft.weekIdx;
    const weekDelta = clampWeekDelta(item, requested - item.weekIdx, plan);
    const weekIdx = item.weekIdx + weekDelta;
    if (weekDelta === 0 && volumeLitres === item.volumeLitres) {
      kept.push(draft.accepted && item.kind === 'rec' ? acceptedRec(item) : item);
      return;
    }
    const drafted = {
      ...item,
      weekIdx,
      brewWeekIdx: item.brewWeekIdx == null ? null : item.brewWeekIdx + weekDelta,
      volumeLitres,
      packagedLitres: packagedFromVolume(volumeLitres, lossPct),
      kind: item.kind === 'rec' ? 'brew' : item.kind,
      draftFromRec: item.kind === 'rec',
      weekDelta,
      edited: true,
    };
    if (item.kind === 'rec') drafted.source = readySource(item, weekDelta);
    kept.push(drafted);
  });
  Object.entries(drafts || {}).forEach(([id, draft]) => {
    if (draft && draft.added && Number(draft.volumeLitres) > 0) {
      kept.push(addedArrival(id, draft, plan));
    }
  });
  return kept;
};

const isRecommendation = (item) => item.kind === 'rec';

/** Arrivals unchanged, so the server series is already exact — avoid any re-sim drift. */
const sameArrivals = (original, adjusted) => {
  if (original.length !== adjusted.length) return false;
  const byId = new Map(adjusted.map((item) => [item.id, item]));
  return original.every((item) => {
    const next = byId.get(item.id);
    return next
      && next.weekIdx === item.weekIdx
      && next.packagedLitres === item.packagedLitres
      && isRecommendation(next) === isRecommendation(item);
  });
};

/**
 * Rebuild the weekly stock line from the current (drafted) arrivals.
 *
 * Stock is NOT linear in arrivals: a stockout can't carry a negative backlog, so each week floors
 * at zero before demand is applied. That means a brew only contributes what survives to a given
 * week, and moving or removing it can't be modelled as a flat per-week delta. So re-simulate the
 * whole horizon the same way the backend planner does (opening stock + arrivals − demand, floored),
 * using the server's opening stock derived from week 0. When nothing changed we return the server
 * series verbatim to avoid rounding drift.
 */
export const projectWeeks = (weeks, original, adjusted) => {
  const n = weeks.length;
  if (!n) return weeks;
  if (sameArrivals(original, adjusted)) return weeks;

  const existing = new Array(n).fill(0);
  const recommended = new Array(n).fill(0);
  adjusted.forEach((item) => {
    const week = item.weekIdx;
    if (week == null || week < 0 || week >= n) return;
    const bucket = isRecommendation(item) ? recommended : existing;
    bucket[week] += item.packagedLitres || 0;
  });

  // Opening (physical) stock is fixed regardless of edits: stockNow = stockEnd0 + demand0 − arrivals0.
  const first = weeks[0];
  const opening = (first.stockEndPlanLitres || 0) + (first.demandLitres || 0)
    - (first.existingArrivalsLitres || 0) - (first.recommendedArrivalsLitres || 0);

  const stock = new Array(n);
  let s = opening;
  for (let i = 0; i < n; i += 1) {
    s += existing[i] + recommended[i];
    s = Math.max(0, s) - (weeks[i].demandLitres || 0);
    stock[i] = s;
  }

  return weeks.map((w, i) => ({
    ...w,
    existingArrivalsLitres: Math.round(existing[i]),
    recommendedArrivalsLitres: Math.round(recommended[i]),
    stockEndPlanLitres: Math.round(stock[i]),
  }));
};

const sum = (values) => values.reduce((total, n) => total + n, 0);

/** Forward demand from week `start`, matching planner cover windows on the stored weekly litres. */
const demandOver = (weeks, start, windowWeeks) => {
  let left = windowWeeks;
  let total = 0;
  const series = weeks || [];
  for (let j = start; left > 1e-9 && j < series.length; j += 1) {
    const take = Math.min(1, left);
    total += (series[j].demandLitres || 0) * take;
    left -= take;
  }
  return total;
};

const openingStock = (weeks) => {
  const first = (weeks && weeks[0]) || {};
  return (first.stockEndPlanLitres || 0) + (first.demandLitres || 0)
    - (first.existingArrivalsLitres || 0) - (first.recommendedArrivalsLitres || 0);
};

/** Litres weights from an explicit pack split. Mix lines and empty splits fall through to the gap. */
const splitWeights = (item, lines) => {
  const pack = item.source && item.source.packSplit;
  if (!Array.isArray(pack) || !pack.length) return null;
  const weights = lines.map((line) => {
    if (line.viaMix) return 0;
    const row = pack.find((entry) => entry && entry.sku === line.sku && !entry.viaMix);
    if (!row) return 0;
    const units = Number(row.units) || 0;
    const per = Number(line.litresPerUnit) || 0;
    return units * (per > 0 ? per : 1);
  });
  return sum(weights) > 0 ? weights : null;
};

const allocateByGap = (lines, lineStock, weekIdx, packagedLitres, windowWeeks) => {
  const needs = lines.map((line, k) => {
    const demand = demandOver(line.weeks, weekIdx, windowWeeks);
    return { demand, gap: Math.max(demand - Math.max(lineStock[k], 0), 0) };
  });
  let weights = needs.map((row) => row.gap);
  if (sum(weights) <= 0) weights = needs.map((row) => row.demand);
  if (sum(weights) <= 0) weights = lines.map((line) => (line.viaMix ? 0 : 1));
  const total = sum(weights) || 1;
  return weights.map((weight) => (packagedLitres * weight) / total);
};

/**
 * Per-SKU stock for the drafted arrivals. Unchanged arrivals keep the server series.
 * Edited arrivals are re-allocated the same way as the planner: explicit pack splits keep their
 * share, everything else is split by shortfall, and each SKU floors at zero before demand.
 */
export const projectSkuWeeks = (skuLines, original, adjusted, maxCoverWeeks) => {
  const lines = skuLines || [];
  if (!lines.length || sameArrivals(original, adjusted)) return lines;

  const windowWeeks = Number(maxCoverWeeks) > 0 ? Number(maxCoverWeeks) : 8;
  const horizon = Math.max(...lines.map((line) => (line.weeks || []).length));
  const stock = lines.map((line) => openingStock(line.weeks));
  const built = lines.map(() => []);

  for (let i = 0; i < horizon; i += 1) {
    const existing = lines.map(() => 0);
    const recommended = lines.map(() => 0);
    (adjusted || []).forEach((item) => {
      if (item.weekIdx !== i) return;
      const litres = item.packagedLitres || 0;
      if (!(litres > 0)) return;
      const weights = splitWeights(item, lines);
      const alloc = weights
        ? weights.map((weight) => (litres * weight) / (sum(weights) || 1))
        : allocateByGap(lines, stock, i, litres, windowWeeks);
      const bucket = isRecommendation(item) ? recommended : existing;
      alloc.forEach((litresIn, k) => {
        stock[k] += litresIn;
        bucket[k] += litresIn;
      });
    });
    lines.forEach((line, k) => {
      const prior = (line.weeks || [])[i] || {};
      const demand = prior.demandLitres || 0;
      const end = Math.max(0, stock[k]) - demand;
      stock[k] = end;
      built[k].push({
        ...prior,
        demandLitres: demand,
        existingArrivalsLitres: Math.round(existing[k]),
        recommendedArrivalsLitres: Math.round(recommended[k]),
        stockEndPlanLitres: Math.round(end),
      });
    });
  }

  return lines.map((line, k) => ({ ...line, weeks: built[k] }));
};

const scaleSplit = (split, ratio) => (split || [])
  .filter((row) => row && row.sku && !row.viaMix && Number(row.units) > 0)
  .map(({ sku, units }) => ({
    sku,
    units: Math.floor(Number(units) * ratio),
  }))
  .filter((row) => row.units > 0);

/**
 * Creates (edited recommendations and new draft brews), partial updates, and
 * deletes of planned brews removed from the chart.
 */
export const draftsToRequests = (original, adjusted) => {
  const creates = [];
  const updates = [];
  const deletes = [];
  const adjustedIds = new Set(adjusted.map((item) => item.id));
  (original || []).forEach((item) => {
    if (adjustedIds.has(item.id)) return;
    if (item.kind === 'brew' && item.source && item.source.id != null) {
      deletes.push(item.source.id);
    }
  });
  adjusted.forEach((item) => {
    if (!item.edited) return;
    const deltaDays = (item.weekDelta || 0) * 7;
    if (item.added) {
      creates.push({
        beerName: item.source.beerName,
        brewery: item.source.brewery,
        volumeLitres: item.volumeLitres,
        brewDate: item.source.brewDate,
        packageDate: item.source.packageDate,
        status: 'planned',
        notes: 'Added from the forecast chart',
      });
      return;
    }
    if (item.draftFromRec) {
      const base = brewFromRecommendation(item.source);
      const tank = item.source.tankLitres;
      const ratio = tank > 0 ? item.volumeLitres / tank : 1;
      creates.push({
        ...base,
        volumeLitres: item.volumeLitres,
        brewDate: addDays(base.brewDate, deltaDays),
        packageDate: base.packageDate ? addDays(base.packageDate, deltaDays) : null,
        packSplit: scaleSplit(base.packSplit, ratio),
        status: 'planned',
      });
      return;
    }
    if (item.kind === 'brew' && item.source && item.source.id != null) {
      const src = item.source;
      const ratio = src.volumeLitres > 0 ? item.volumeLitres / src.volumeLitres : 1;
      const update = {
        id: src.id,
        volumeLitres: item.volumeLitres,
        brewDate: addDays(src.brewDate, deltaDays),
      };
      if (src.packageDate) update.packageDate = addDays(src.packageDate, deltaDays);
      if (Array.isArray(src.packSplit) && src.packSplit.length) {
        update.packSplit = scaleSplit(src.packSplit, ratio);
      }
      updates.push(update);
    }
  });
  return { creates, updates, deletes };
};
